import { createSlice } from "@reduxjs/toolkit";
import { OrdersState } from "./orders.state";
import {
  getOrders,
  getOrderSummary,
  getOrderDetail,
  getCancelInfo,
  cancelOrderThunk,
  reorderThunk,
  getReturnInfo,
  getReturnPreview,
  submitReturn,
} from "./orders.thunk";
import { toggleStoreFollow } from "../store/store.thunk";

const initialState: OrdersState = {
  data: null,
  status: "idle",
  error: null,
  orderDetail: null,
  orderDetailStatus: "idle",
  summary: null,
  summaryStatus: "idle",
  cancelInfo: null,
  cancelInfoStatus: "idle",
  cancelResult: null,
  returnInfo: null,
  returnInfoStatus: "idle",
  returnPreview: null,
  returnPreviewStatus: "idle",
  returnResult: null,
  currentPage: 1,
  hasMore: true,
  filters: {},
};

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    resetOrders: (state) => {
      state.data = null;
      state.status = "idle";
      state.error = null;
      state.currentPage = 1;
      state.hasMore = true;
    },
    resetOrderDetail: (state) => {
      state.orderDetail = null;
      state.orderDetailStatus = "idle";
    },
    resetCancelInfo: (state) => {
      state.cancelInfo = null;
      state.cancelInfoStatus = "idle";
      state.cancelResult = null;
    },
    resetReturnInfo: (state) => {
      state.returnInfo = null;
      state.returnInfoStatus = "idle";
      state.returnResult = null;
    },
    resetReturnPreview: (state) => {
      state.returnPreview = null;
      state.returnPreviewStatus = "idle";
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
      state.currentPage = 1;
      state.data = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Orders
    builder
      .addCase(getOrders.pending, (state, action) => {
        const isFirstPage = !action.meta.arg || action.meta.arg.page === 1;

        if (isFirstPage) {
          state.data = null;
        }

        state.status = "loading";
        state.error = null;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.status = "success";
        if (action.payload.data.pagination.current_page === 1) {
          state.data = action.payload.data;
        } else if (state.data) {
          state.data = {
            ...action.payload.data,
            items: [...state.data.items, ...action.payload.data.items],
          };
        } else {
          state.data = action.payload.data;
        }
        state.currentPage = action.payload.data.pagination.current_page;
        state.hasMore = action.payload.data.pagination.next_page_url !== null;
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.status = "error";
        state.error =
          action.payload ?? "Siparişler yüklenirken bir hata oluştu.";
      });

    // Get Order Summary
    builder
      .addCase(getOrderSummary.pending, (state) => {
        state.summaryStatus = "loading";
      })
      .addCase(getOrderSummary.fulfilled, (state, action) => {
        state.summaryStatus = "success";
        state.summary = action.payload.data;
      })
      .addCase(getOrderSummary.rejected, (state) => {
        state.summaryStatus = "error";
      });

    // Get Order Detail
    builder
      .addCase(getOrderDetail.pending, (state) => {
        state.orderDetailStatus = "loading";
        state.orderDetail = null;
      })
      .addCase(getOrderDetail.fulfilled, (state, action) => {
        state.orderDetailStatus = "success";
        state.orderDetail = action.payload.data.order;
      })
      .addCase(getOrderDetail.rejected, (state) => {
        state.orderDetailStatus = "error";
        state.orderDetail = null;
      });

    // Get Cancel Info
    builder
      .addCase(getCancelInfo.pending, (state) => {
        state.cancelInfoStatus = "loading";
        state.cancelInfo = null;
      })
      .addCase(getCancelInfo.fulfilled, (state, action) => {
        state.cancelInfoStatus = "success";
        state.cancelInfo = action.payload.data;
      })
      .addCase(getCancelInfo.rejected, (state) => {
        state.cancelInfoStatus = "error";
        state.cancelInfo = null;
      });

    // Cancel Order
    builder
      .addCase(cancelOrderThunk.pending, (state) => {
        state.cancelResult = null;
      })
      .addCase(cancelOrderThunk.fulfilled, (state, action) => {
        state.cancelResult = action.payload.data;
      })
      .addCase(cancelOrderThunk.rejected, (state) => {
        state.cancelResult = null;
      });

    // Reorder
    builder.addCase(reorderThunk.pending, () => {
      // Loading handled in component
    });

    // Get Return Info
    builder
      .addCase(getReturnInfo.pending, (state) => {
        state.returnInfoStatus = "loading";
        state.returnInfo = null;
      })
      .addCase(getReturnInfo.fulfilled, (state, action) => {
        state.returnInfoStatus = "success";
        state.returnInfo = action.payload.data;
      })
      .addCase(getReturnInfo.rejected, (state) => {
        state.returnInfoStatus = "error";
        state.returnInfo = null;
      });

    // Get Return Preview
    builder
      .addCase(getReturnPreview.pending, (state) => {
        state.returnPreviewStatus = "loading";
        state.returnPreview = null;
      })
      .addCase(getReturnPreview.fulfilled, (state, action) => {
        state.returnPreviewStatus = "success";
        state.returnPreview = action.payload.data;
      })
      .addCase(getReturnPreview.rejected, (state) => {
        state.returnPreviewStatus = "error";
        state.returnPreview = null;
      });

    // Submit Return
    builder
      .addCase(submitReturn.pending, (state) => {
        state.returnResult = null;
      })
      .addCase(submitReturn.fulfilled, (state, action) => {
        state.returnResult = action.payload.data;
      })
      .addCase(submitReturn.rejected, (state) => {
        state.returnResult = null;
      })

      .addCase(toggleStoreFollow.fulfilled, (state, action) => {
        if (!state.orderDetail) return;

        state.orderDetail.sellers = state.orderDetail.sellers.map((seller) => {
          if (seller.seller_id !== action.meta.arg) return seller;

          return {
            ...seller,
            is_followed: action.payload.is_following,
            button_list: seller.button_list.map((button) => {
              if (button.type !== "follow_us") return button;
              return {
                ...button,
                is_followed: action.payload.is_following,
                text: action.payload.is_following ? "Takibi Bırak" : "Takip Et",
              };
            }),
          };
        });
      });
  },
});

export const {
  resetOrders,
  resetOrderDetail,
  resetCancelInfo,
  resetReturnInfo,
  resetReturnPreview,
  setFilters,
  setCurrentPage,
} = ordersSlice.actions;

export default ordersSlice.reducer;
